import React, { useEffect } from 'react';
import { X as XIcon } from 'lucide-react';
import { FaXTwitter, FaInstagram, FaFacebook, FaYoutube, FaReddit } from 'react-icons/fa6';
import './InfoModal.css';

const InfoModal = ({ type, onClose }) => {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key !== 'Escape') return;
      onClose();
      setTimeout(() => document.activeElement?.blur(), 0);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const getModalContent = () => {
    switch(type) {
      case 'disclaimer':
        return {
          title: 'Disclaimer',
          content: (
            <>
              <p>The information provided on KRIME is for general informational purposes only. While we strive to provide accurate and up-to-date crime statistics, we make no representations or warranties of any kind, express or implied, about the completeness, accuracy, reliability, suitability or availability of the information.</p>
              <p>Crime data is sourced from official government databases and may be subject to reporting delays, revisions, or variations in methodology. Users should not rely solely on this information for making critical decisions about personal safety or property transactions.</p>
              <p>KRIME is not responsible for any decisions made based on the information provided. For the most current and detailed information, please consult official law enforcement sources.</p>
            </>
          )
        };
      case 'terms':
        return {
          title: 'Terms of Use',
          content: (
            <>
              <h3>1. Acceptance of Terms</h3>
              <p>By accessing and using KRIME, you accept and agree to be bound by the terms and provisions of this agreement.</p>
              
              <h3>2. Use License</h3>
              <p>Permission is granted to temporarily access the materials on KRIME for personal, non-commercial use only.</p>
              
              <h3>3. Account Responsibilities</h3>
              <p>You are responsible for maintaining the confidentiality of your account and password. You agree to accept responsibility for all activities that occur under your account.</p>
              
              <h3>4. Prohibited Uses</h3>
              <p>You may not use KRIME to: violate any laws, distribute malware, harass others, or attempt to gain unauthorized access to our systems.</p>
              
              <h3>5. Modifications</h3>
              <p>KRIME reserves the right to modify these terms at any time. Continued use of the service constitutes acceptance of modified terms.</p>
            </>
          )
        };
      case 'privacy':
        return {
          title: 'Privacy Policy',
          content: (
            <>
              <h3>Information We Collect</h3>
              <p>We collect information you provide directly to us, including your email address, account credentials, and usage data.</p>
              
              <h3>How We Use Your Information</h3>
              <p>We use the information we collect to provide, maintain, and improve our services, communicate with you, and protect against fraudulent or illegal activity.</p>
              
              <h3>Data Security</h3>
              <p>We implement appropriate security measures to protect your personal information. However, no method of transmission over the Internet is 100% secure.</p>
              
              <h3>Third-Party Services</h3>
              <p>We use third-party services for authentication (Supabase) and payment processing (Stripe). These services have their own privacy policies.</p>
              
              <h3>Your Rights</h3>
              <p>You have the right to access, correct, or delete your personal information. Contact us to exercise these rights.</p>
            </>
          )
        };
      case 'contact':
        return {
          title: 'Contact Information',
          content: (
            <>
              <p>For press or media inquiries: <a href="mailto:press@krime.app">press@krime.app</a></p>
              <p>For general questions or feedback: <a href="mailto:support@krime.app">support@krime.app</a></p>
              <p>For enterprise solutions or partnerships: <a href="mailto:sales@krime.app">sales@krime.app</a></p>
              
              <h3>Social Media</h3>
              <div className="social-links-modal">
                <a href="https://x.com/krimeapp" target="_blank" rel="noopener noreferrer" className="social-link">
                  <FaXTwitter size={24} />
                  <span>X (Twitter)</span>
                </a>
                <a href="https://youtube.com/@krimeapp" target="_blank" rel="noopener noreferrer" className="social-link">
                  <FaYoutube size={24} />
                  <span>YouTube</span>
                </a>
                <a href="https://instagram.com/krimeapp" target="_blank" rel="noopener noreferrer" className="social-link">
                  <FaInstagram size={24} />
                  <span>Instagram</span>
                </a>
                <a href="https://facebook.com/krimeapp" target="_blank" rel="noopener noreferrer" className="social-link">
                  <FaFacebook size={24} />
                  <span>Facebook</span>
                </a>
                <a href="https://reddit.com/r/krimeapp" target="_blank" rel="noopener noreferrer" className="social-link">
                  <FaReddit size={24} />
                  <span>Reddit</span>
                </a>
              </div>
            </>
          )
        };
      default:
        return null;
    }
  };

  const content = getModalContent();
  if (!content) return null;

  return (
    <div className="info-modal-overlay" onClick={onClose}>
      <div className="info-modal" onClick={(e) => e.stopPropagation()}>
        <button className="info-modal-close" onClick={onClose}>
          <XIcon size={24} />
        </button>
        <div className="info-modal-scroll">
          <h2>{content.title}</h2>
          <div className="info-modal-content">
            {content.content}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InfoModal;